import { buttonBlockSchema } from '../../blocks/button/index.js';
import { dividerBlockSchema } from '../../blocks/divider/index.js';
import { headingBlockSchema } from '../../blocks/heading/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
import { spacerBlockSchema } from '../../blocks/spacer/index.js';
import { textBlockSchema } from '../../blocks/text/index.js';
import { welcomeConsentBlockSchema } from '../../blocks/welcome-consent/index.js';
import { welcomeFieldBlockSchema } from '../../blocks/welcome-field/index.js';
import { welcomeSubmitBlockSchema } from '../../blocks/welcome-submit/index.js';
/**
 * `welcome-popup` — LA FENÊTRE DE BIENVENUE, devenue un document de l'éditeur (feature 0074, lot B).
 *
 * Jusqu'au 18/10/2026 au matin, cette fenêtre était un formulaire à champs fixes dans le tableau de
 * bord : un titre, un sous-titre, un bouton, trois jeux de couleurs. La fondatrice a montré l'éditeur de
 * formulaires de Klaviyo — « elle offre plusieurs design, et une meilleure configuration de la form » —
 * et la réponse n'est pas d'ajouter des champs à ce formulaire : c'est de donner à la fenêtre ce que
 * toutes nos pages ont déjà, un ARBRE DE BLOCS qu'on compose.
 *
 * Ce que la SECTION possède, c'est la CARTE : sa largeur, ses coins, ses couleurs, son voile, et le
 * panneau d'image qui l'accompagne. Tout ce qu'il y a DEDANS est un bloc — titre, texte, champ, bouton,
 * mention de consentement, séparateur, espace — que la vendeuse ajoute, déplace et supprime.
 *
 * **Une seule par document, et ce document ne vit que dans ce gabarit** (`templates`). Deux fenêtres
 * superposées n'ont pas de sens, et une fenêtre sur la page d'accueil serait un deuxième formulaire
 * d'inscription que personne n'a demandé.
 *
 * **Ce qui n'est PAS ici** : le moment où elle s'ouvre, le code de réduction et l'interrupteur. C'est du
 * comportement, pas du design ; ça reste dans la configuration de l'app « Courriels de marque », que la
 * vitrine lit déjà sans dépendre du module des parcours.
 */
const WELCOME_ACCEPTS = [
    'heading',
    'text',
    'image',
    'welcome-field',
    'welcome-submit',
    'welcome-consent',
    'button',
    'divider',
    'spacer',
];
export const welcomePopupSchema = {
    type: 'welcome-popup',
    name: 'Welcome popup',
    version: 1,
    templates: ['welcome-popup'],
    accepts: [...WELCOME_ACCEPTS],
    // Les SCHÉMAS que le conteneur peut instancier — la même liste que `accepts`. Requis pour que
    // l'instanciateur récursif sème les blocs du préréglage et que l'éditeur sache les ajouter.
    blocks: [
        headingBlockSchema,
        textBlockSchema,
        imageBlockSchema,
        welcomeFieldBlockSchema,
        welcomeSubmitBlockSchema,
        welcomeConsentBlockSchema,
        buttonBlockSchema,
        dividerBlockSchema,
        spacerBlockSchema,
    ],
    maxBlocks: 20,
    fields: [
        // ── La carte ────────────────────────────────────────────────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            info: 'Where the picture sits. Without a picture, every layout paints the same card.',
            default: 'plain',
            options: [
                { value: 'plain', label: 'No picture' },
                { value: 'image-top', label: 'Picture on top' },
                { value: 'image-start', label: 'Picture on the left' },
                { value: 'image-end', label: 'Picture on the right' },
            ],
        },
        {
            id: 'image',
            type: 'image',
            label: 'Picture',
            visibleWhen: { field: 'layout', notEquals: 'plain' },
        },
        {
            id: 'mediaHeight',
            type: 'range',
            label: 'Picture height',
            info: '0 = the picture keeps its own shape and nothing is cut. Any other value crops it to that height.',
            min: 0,
            max: 420,
            step: 10,
            default: 0,
            unit: 'px',
            visibleWhen: { field: 'layout', equals: 'image-top' },
        },
        {
            id: 'mediaWidth',
            type: 'range',
            label: 'Picture width',
            info: 'Share of the card the picture takes on a desktop screen. On a phone it always goes on top.',
            min: 25,
            max: 60,
            step: 5,
            default: 45,
            unit: '%',
            visibleWhen: { field: 'layout', equals: ['image-start', 'image-end'] },
        },
        {
            id: 'width',
            type: 'range',
            label: 'Width',
            info: 'How wide the card is allowed to grow. It never exceeds the screen.',
            min: 320,
            max: 760,
            step: 10,
            default: 440,
            unit: 'px',
        },
        {
            id: 'cornerRadius',
            type: 'range',
            label: 'Corner radius',
            min: 0,
            max: 32,
            step: 1,
            default: 16,
            unit: 'px',
        },
        {
            id: 'contentAlignment',
            type: 'select',
            label: 'Text alignment',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'showClose',
            type: 'checkbox',
            label: 'Show the close cross',
            info: 'Turning it off does not trap the visitor: clicking outside the card and pressing Escape still close it.',
            default: true,
        },
        // ── Les couleurs ────────────────────────────────────────────────────────────────────────────────
        {
            id: 'backgroundColor',
            type: 'color',
            label: 'Card background',
            group: 'Colors',
            default: { ref: 'custom', value: '#FFFFFF' },
        },
        {
            id: 'textColor',
            type: 'color',
            label: 'Card text',
            group: 'Colors',
            default: { ref: 'custom', value: '#151515' },
        },
        {
            id: 'overlayColor',
            type: 'color',
            label: 'Dim behind the card',
            group: 'Colors',
            default: { ref: 'custom', value: '#151515' },
        },
        {
            id: 'overlayOpacity',
            type: 'range',
            label: 'How dark',
            group: 'Colors',
            min: 0,
            max: 90,
            step: 2,
            default: 52,
            unit: '%',
        },
        // ── Les marges intérieures ──────────────────────────────────────────────────────────────────────
        {
            id: 'paddingX',
            type: 'range',
            label: 'Side padding',
            group: 'Padding',
            min: 8,
            max: 56,
            step: 2,
            default: 24,
            unit: 'px',
        },
        {
            id: 'paddingY',
            type: 'range',
            label: 'Top and bottom padding',
            group: 'Padding',
            min: 8,
            max: 56,
            step: 2,
            default: 26,
            unit: 'px',
        },
    ],
};
/**
 * LE préréglage — et il n'y en a qu'un (décision de la fondatrice, 18/10/2026 : « oublie la partie de
 * créer des modèles, on va faire juste un modèle par défaut qui va s'ouvrir dans le builder, c'est aux
 * seller de le configurer »). C'est aussi ce que « Ajouter une section » insère si la fenêtre a été
 * supprimée du document.
 *
 * **Les phrases sont des MARQUEURS `@t:`, pas du français.** Elles sont traduites au moment de
 * l'insertion, dans la langue de la boutique, par `hydrateDocument`. Écrites en clair, elles figeraient
 * « Rejoignez notre liste » dans le document d'une boutique arabe pour toujours.
 */
export const welcomePopupPresets = [
    {
        name: 'Welcome popup',
        settings: {
            layout: 'plain',
            width: 440,
            cornerRadius: 16,
            contentAlignment: 'center',
            showClose: true,
            overlayOpacity: 52,
            paddingX: 24,
            paddingY: 26,
        },
        blocks: [
            {
                type: 'heading',
                settings: {
                    title: { format: 'html', html: '@t:welcome_popup.title' },
                    headingSize: 'h2',
                    alignment: 'center',
                    mobileAlignment: 'mobile-center',
                    marginTop: 0,
                    marginBottom: 6,
                },
            },
            {
                type: 'text',
                settings: {
                    text: { format: 'html', html: '@t:welcome_popup.subtitle' },
                    alignment: 'center',
                    mobileAlignment: 'center',
                    marginTop: 0,
                    marginBottom: 14,
                },
            },
            { type: 'welcome-field', settings: { kind: 'email', required: true } },
            { type: 'welcome-submit', settings: {} },
            { type: 'welcome-consent', settings: {} },
        ],
    },
];
export const welcomePopupMeta = {
    label: 'Welcome popup',
    group: 'Promotion',
    icon: 'mail-open',
};
//# sourceMappingURL=schema.js.map